import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2 } from 'lucide-react';

const GRADE_POINTS = {
  'A+': 4.0, 'A': 4.0, 'A-': 3.7,
  'B+': 3.3, 'B': 3.0, 'B-': 2.7,
  'C+': 2.3, 'C': 2.0, 'C-': 1.7,
  'D+': 1.3, 'D': 1.0, 'F': 0.0,
};

let courseId = 0;
const newCourse = (name = '', credits = 3, grade = 'A') => ({ id: ++courseId, name, credits, grade });

const GPACalculator = () => {
  const [courses, setCourses] = useState([
    newCourse('Mathematics', 4, 'A'),
    newCourse('Physics', 3, 'B+'),
    newCourse('English', 3, 'A-'),
    newCourse('Computer Science', 4, 'A+'),
  ]);

  const { gpa, totalCredits, totalPoints } = useMemo(() => {
    let tc = 0, tp = 0;
    courses.forEach(c => {
      const cr = parseFloat(c.credits);
      const pts = GRADE_POINTS[c.grade];
      if (!isNaN(cr) && pts !== undefined) {
        tc += cr;
        tp += cr * pts;
      }
    });
    return { gpa: tc > 0 ? tp / tc : 0, totalCredits: tc, totalPoints: tp };
  }, [courses]);

  const update = (cid, field, value) => setCourses(courses.map(c => c.id === cid ? { ...c, [field]: value } : c));
  const remove = (cid) => setCourses(courses.filter(c => c.id !== cid));
  const add = () => setCourses([...courses, newCourse()]);

  return (
    <CalculatorLayout
      title="GPA Calculator"
      subtitle="Compute GPA on a 4.0 scale with letter grades and course credits."
      category="Other"
      description={<p>Enter each course, its credit hours, and letter grade. GPA is calculated as the weighted average of grade points. Standard US 4.0 scale with plus/minus grades is used.</p>}
      formulaNote="GPA = Σ(credits × grade points) / Σ(credits)"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-6">
        <div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 text-xs">Course</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600 text-xs w-24">Credits</th>
                  <th className="px-3 py-2 text-center font-semibold text-slate-600 text-xs w-28">Grade</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {courses.map(c => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-2 py-2">
                      <Input value={c.name} onChange={(e) => update(c.id, 'name', e.target.value)} placeholder="Course name" className="h-9 border-slate-200" />
                    </td>
                    <td className="px-2 py-2">
                      <Input type="number" step="0.5" value={c.credits} onChange={(e) => update(c.id, 'credits', e.target.value)} className="h-9 text-right border-slate-200" />
                    </td>
                    <td className="px-2 py-2">
                      <Select value={c.grade} onValueChange={(v) => update(c.id, 'grade', v)}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.keys(GRADE_POINTS).map(g => <SelectItem key={g} value={g}>{g} ({GRADE_POINTS[g].toFixed(1)})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-1">
                      <Button variant="ghost" size="icon" onClick={() => remove(c.id)} className="h-8 w-8 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button variant="outline" onClick={add} className="mt-3"><Plus className="w-4 h-4 mr-1" /> Add Course</Button>
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5 h-fit">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Your GPA</div>
          <div className="font-display text-5xl font-bold text-bharat-navy display-font mt-1">{gpa.toFixed(2)}</div>
          <div className="text-xs text-slate-500">out of 4.00</div>
          <div className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Total Credits</span><span className="display-font font-semibold">{totalCredits}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Grade Points</span><span className="display-font font-semibold">{totalPoints.toFixed(2)}</span></div>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

export default GPACalculator;
